import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, Snackbar, Alert, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
  MenuItem, Grid, Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import config from '../../../config';

// HR Service Charge — monthly pool collected from restaurant/room billing.
// Admin enters the pool amount, then opens distribution dialog to set per-
// employee shares (percentage based). Amount auto-derives from pool × pct.
// Payroll generation reads these shares as ServiceShare for the month.
export default function ServiceCharge() {
  const token = sessionStorage.getItem('token');
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const companyId = company?.id;
  const now = new Date();

  const [year, setYear] = useState(now.getFullYear());
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  const [poolDialog, setPoolDialog] = useState(false);
  const [poolForm, setPoolForm] = useState({
    month: now.getMonth() + 1, year, totalCollected: 0, remarks: '',
  });

  const [distDialog, setDistDialog] = useState(false);
  const [distTarget, setDistTarget] = useState(null);
  const [distRows, setDistRows] = useState([]); // [{employeeId, sharePercent, include}]

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const toast = (m, s = 'success') => setSnack({ open: true, msg: m, sev: s });

  const loadEmployees = async () => {
    const res = await fetch(`${config.API_BASE_URL}/Hr/Employees/${companyId}`,
      { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setEmployees(await res.json());
  };

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/Hr/ServiceCharges/${companyId}?year=${year}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error();
      setRows(await res.json());
    } catch (e) { toast('Failed to load', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (companyId) loadEmployees(); /* eslint-disable-next-line */ }, [companyId]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId, year]);

  const savePool = async () => {
    const body = {
      Id: null,
      CompanyId: companyId,
      Month: Number(poolForm.month),
      Year: Number(poolForm.year),
      TotalCollected: Number(poolForm.totalCollected) || 0,
      Remarks: poolForm.remarks || null,
    };
    const res = await fetch(`${config.API_BASE_URL}/Hr/ServiceCharge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || !data.success) { toast('Save failed', 'error'); return; }
    toast('Pool saved');
    setPoolDialog(false);
    load();
  };

  const openDistribute = async (r) => {
    setDistTarget(r);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/Hr/ServiceChargeDistribution/${r.id}`,
        { headers: { Authorization: `Bearer ${token}` } });
      const existing = res.ok ? await res.json() : [];
      const existingMap = new Map(existing.map((e) => [e.employeeId, e]));
      // Seed: all active employees, marked included if they had a share.
      setDistRows(employees.map((e) => ({
        employeeId: e.id,
        employeeName: e.employeeName,
        employeeCode: e.employeeCode,
        sharePercent: existingMap.get(e.id)?.sharePercent || 0,
        include: existingMap.has(e.id),
      })));
    } catch (e) { setDistRows([]); }
    setDistDialog(true);
  };

  const equalSplit = () => {
    const included = distRows.filter((r) => r.include);
    if (included.length === 0) return;
    const pct = Math.round((100 / included.length) * 100) / 100;
    setDistRows((prev) => prev.map((r) =>
      r.include ? { ...r, sharePercent: pct } : r));
  };

  const totalPct = distRows.filter((r) => r.include).reduce((a, r) => a + Number(r.sharePercent || 0), 0);
  const totalAmt = distTarget ? (Number(distTarget.totalCollected || 0) * totalPct / 100) : 0;

  const saveDistribution = async () => {
    if (Math.abs(totalPct - 100) > 0.5) {
      if (!window.confirm(`Total = ${totalPct.toFixed(2)}%. Save anyway?`)) return;
    }
    const body = {
      CompanyId: companyId,
      ServiceChargeId: distTarget.id,
      Rows: distRows.filter((r) => r.include && r.sharePercent > 0).map((r) => ({
        EmployeeId: r.employeeId,
        SharePercent: Number(r.sharePercent) || 0,
      })),
    };
    const res = await fetch(`${config.API_BASE_URL}/Hr/ServiceChargeDistribute`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok || !data.success) { toast('Save failed', 'error'); return; }
    toast('Distribution saved');
    setDistDialog(false);
    load();
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#F1F5F9', minHeight: '100%' }}>
      <Stack direction='row' spacing={1.5} alignItems='center' sx={{ mb: 2, justifyContent: 'space-between' }}>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box sx={{ width: 42, height: 42, borderRadius: 2, bgcolor: '#0891b2', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <RestaurantIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>Service Charge</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>HR · Monthly pool + distribution</Typography>
          </Box>
        </Stack>
        <Stack direction='row' spacing={1.5}>
          <TextField type='number' size='small' label='Year' value={year} onChange={(e) => setYear(Number(e.target.value))} sx={{ bgcolor: '#fff', width: 110 }} />
          <Button variant='contained' startIcon={<AddIcon />}
            onClick={() => { setPoolForm({ month: now.getMonth() + 1, year, totalCollected: 0, remarks: '' }); setPoolDialog(true); }}
            sx={{ bgcolor: '#0891b2', '&:hover': { bgcolor: '#0e7490' } }}>
            New Pool
          </Button>
        </Stack>
      </Stack>

      <Card sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
          ) : (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Period</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>Collected</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>Distributed</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>Pending</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'># Staff</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Remarks</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow><TableCell colSpan={7} align='center' sx={{ py: 4 }}><Typography sx={{ color: '#94a3b8' }}>No pools for this year.</Typography></TableCell></TableRow>
                  )}
                  {rows.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {new Date(r.year, r.month - 1, 1).toLocaleString('en-IN', { month: 'long' })} {r.year}
                      </TableCell>
                      <TableCell align='right' sx={{ fontWeight: 700 }}>₹{Number(r.totalCollected).toLocaleString('en-IN')}</TableCell>
                      <TableCell align='right' sx={{ color: '#16a34a' }}>₹{Number(r.totalDistributed).toLocaleString('en-IN')}</TableCell>
                      <TableCell align='right' sx={{ color: r.pending > 0.5 ? '#dc2626' : '#94a3b8' }}>₹{Number(r.pending).toLocaleString('en-IN')}</TableCell>
                      <TableCell align='right'>{r.distributionCount}</TableCell>
                      <TableCell sx={{ color: '#475569' }}>{r.remarks || '—'}</TableCell>
                      <TableCell align='right'>
                        <Tooltip title='Distribute'><IconButton size='small' onClick={() => openDistribute(r)}><VisibilityIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Pool dialog */}
      <Dialog open={poolDialog} onClose={() => setPoolDialog(false)} maxWidth='sm' fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Service Charge Pool</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={6}>
              <TextField select size='small' fullWidth label='Month *' value={poolForm.month}
                onChange={(e) => setPoolForm({ ...poolForm, month: e.target.value })}>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <MenuItem key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString('en-IN', { month: 'long' })}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField type='number' size='small' fullWidth label='Year *' value={poolForm.year}
                onChange={(e) => setPoolForm({ ...poolForm, year: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField type='number' size='small' fullWidth label='Total Collected *' value={poolForm.totalCollected}
                onChange={(e) => setPoolForm({ ...poolForm, totalCollected: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField size='small' fullWidth multiline rows={2} label='Remarks' value={poolForm.remarks}
                onChange={(e) => setPoolForm({ ...poolForm, remarks: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setPoolDialog(false)}>Cancel</Button>
          <Button variant='contained' onClick={savePool}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Distribution dialog */}
      <Dialog open={distDialog} onClose={() => setDistDialog(false)} maxWidth='md' fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Distribute Service Charge
          {distTarget && (
            <Typography sx={{ fontSize: '0.85rem', color: '#64748B' }}>
              {new Date(distTarget.year, distTarget.month - 1, 1).toLocaleString('en-IN', { month: 'long' })} {distTarget.year} · Pool ₹{Number(distTarget.totalCollected).toLocaleString('en-IN')}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Stack direction='row' spacing={1.5} sx={{ mb: 1.5, alignItems: 'center' }}>
            <Button size='small' variant='outlined' onClick={equalSplit}>Equal Split (included)</Button>
            <Box sx={{ flex: 1 }} />
            <Chip label={`Total: ${totalPct.toFixed(2)}% · ₹${totalAmt.toLocaleString('en-IN')}`}
              sx={{ bgcolor: Math.abs(totalPct - 100) < 0.5 ? '#dcfce7' : '#fee2e2',
                color: Math.abs(totalPct - 100) < 0.5 ? '#166534' : '#991b1b', fontWeight: 700 }} />
          </Stack>
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size='small' stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding='checkbox'>
                    <Checkbox size='small'
                      checked={distRows.length > 0 && distRows.every((r) => r.include)}
                      indeterminate={distRows.some((r) => r.include) && !distRows.every((r) => r.include)}
                      onChange={(e) => setDistRows((prev) => prev.map((r) => ({ ...r, include: e.target.checked })))} />
                  </TableCell>
                  <TableCell>Employee</TableCell>
                  <TableCell align='right'>Share %</TableCell>
                  <TableCell align='right'>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {distRows.map((r, idx) => (
                  <TableRow key={r.employeeId}>
                    <TableCell padding='checkbox'>
                      <Checkbox size='small' checked={r.include}
                        onChange={(e) => setDistRows((prev) => {
                          const next = [...prev]; next[idx] = { ...next[idx], include: e.target.checked }; return next;
                        })} />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {r.employeeName}
                      <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>{r.employeeCode}</Typography>
                    </TableCell>
                    <TableCell align='right'>
                      <TextField type='number' size='small' value={r.sharePercent}
                        disabled={!r.include}
                        onChange={(e) => setDistRows((prev) => {
                          const next = [...prev]; next[idx] = { ...next[idx], sharePercent: e.target.value }; return next;
                        })}
                        inputProps={{ min: 0, max: 100, step: 0.01 }}
                        sx={{ width: 100 }} />
                    </TableCell>
                    <TableCell align='right'>
                      ₹{((Number(r.sharePercent || 0) * Number(distTarget?.totalCollected || 0)) / 100).toLocaleString('en-IN')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDistDialog(false)}>Cancel</Button>
          <Button variant='contained' onClick={saveDistribution} sx={{ bgcolor: '#0891b2', '&:hover': { bgcolor: '#0e7490' } }}>
            Save Distribution
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.sev} variant='filled'>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
